import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

/**
 *  JOGSZABÁLYFIGYELŐ – fejlesztői, nem publikus modul (/jogszabalyfigyelo, fejlesztoGuard).
 *
 *  A jogszabalyfigyelo/ szolgáltatás (Közlöny, Nemzeti Jogszabálytár, Országgyűlés, társadalmi
 *  egyeztetés, NAV → Claude-elemzés) kimutatása a nyilvántartón belül: a kiszolgáló
 *  (backend/jogszabalyfigyelo.js) csak továbbítja az adatot. A tételenkénti saját jelölés
 *  (Átnézve / Feladat kiadva / Kész / Nem érint) ugyanaz, mint a figyelő saját oldalán.
 */

const ALLAPOTOK: [string, string][] = [
  ['pletyka', 'Sajtóhír'], ['bejelentve', 'Bejelentve'], ['tervezet', 'Tervezet'], ['benyujtva', 'Benyújtva'],
  ['elfogadva', 'Elfogadva'], ['kihirdetve', 'Kihirdetve'], ['hatalyos', 'Hatályos'],
];
const ALL_NEV: Record<string, string> = Object.fromEntries([...ALLAPOTOK, ['elvetve', 'Elvetve / visszavonva']]);
const FONT_SORREND: Record<string, number> = { magas: 0, kozepes: 1, alacsony: 2 };

type Lap = 'attekintes' | 'valtozasok' | 'oktatas' | 'jogszabalyok' | 'naplo' | 'rendszer';

/** A tanulmányi rendszer követelményeinek megfelelési állapota (jogszabalyfigyelo/kovetelmenyek.json). */
const MEGFELELES: Record<string, [string, string]> = {
  megfelel: ['zold', 'Megfelel'], reszben: ['sarga', 'Részben'], nem: ['piros', 'Nem felel meg'],
  nem_vizsgalt: ['', 'Nem vizsgált'], nem_alkalmazando: ['', 'Nem alkalmazandó'],
};
const KIRE: Record<string, string> = { szoftver: 'a programra', intezmeny: 'az intézményre', mindketto: 'programra és intézményre' };

/** Kézzel indítható körök (jogszabalyfigyelo/futtat.sh). */
const MODOK: [string, string][] = [
  ['oktatas', 'Oktatási kör – a követelmények a kóddal szemben'],
  ['napi', 'Napi kör – minden forrás + elemzés'],
  ['heti', 'Heti kör – alapos, bejelentés-söpréssel'],
  ['gyakori', 'Gyors kör – gyűjtés, új jelzések'],
  ['hatralek', 'Hátralék – a várakozó jelzések feldolgozása'],
  ['oldal', 'Csak a kimutatás újraépítése'],
];

@Component({
  selector: 'app-jogszabalyfigyelo',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './jogszabalyfigyelo.component.html',
  styleUrls: ['./jogszabalyfigyelo.component.scss'],
})
export class JogszabalyfigyeloComponent implements OnInit, OnDestroy {
  adat: any = null;
  hiba = '';
  tolt = false;
  lap: Lap = 'attekintes';
  ma = new Date().toISOString().slice(0, 10);

  readonly allapotok = ALLAPOTOK;
  readonly sajatOpciok: [string, string][] = [['', 'Új'], ['atnezve', 'Átnézve'], ['feladat', 'Feladat kiadva'], ['kesz', 'Kész / beépítve'], ['nem_erint', 'Nem érint']];
  sz = { q: '', modul: '', allapot: '', font: '', sajat: 'nyitott', rend: 'teendo' };
  osz = { q: '', kat: '', mf: '' };
  readonly megfelelesek = Object.entries(MEGFELELES);
  masolva = '';

  /*  FUTTATÁS ÉS ÉLŐ KÖVETÉS (2026-10-15, a user kérése): kör indítása innen, és a futás naplója
   *  élőben (a figyelő szerverének /api/futas végpontja, a backend továbbítja). */
  readonly modok = MODOK;
  futMod = 'oktatas';
  futas: any = null;
  futasNyitva = false;
  inditas = false;
  futHiba = '';
  private futasIdozito: any = null;
  private elozoFut = false;
  @ViewChild('futNaplo') futNaplo?: ElementRef<HTMLDivElement>;

  constructor(private http: HttpClient) {}

  async ngOnInit() {
    try { const l = localStorage.getItem('jf-lap') as Lap; if (l) this.lap = l; } catch { /* nincs tár */ }
    await this.betolt();
    this.futasFrissit();
  }

  ngOnDestroy() { clearTimeout(this.futasIdozito); }

  modNev(m: string) { return MODOK.find(([k]) => k === m)?.[1] || m; }

  /** A futás állapota; fut közben 2, egyébként 15 másodpercenként (az időzített köröket is elkapja). */
  async futasFrissit() {
    clearTimeout(this.futasIdozito);
    try {
      const f: any = await firstValueFrom(this.http.get('/api/jogszabalyfigyelo/futas'));
      const lentVolt = this.futNaplo ? this.futNaplo.nativeElement.scrollHeight - this.futNaplo.nativeElement.scrollTop - this.futNaplo.nativeElement.clientHeight < 40 : true;
      this.futas = f;
      if (f.fut) this.futasNyitva = true;
      if (this.elozoFut && !f.fut) this.betolt();       // véget ért: az új eredmények betöltése
      this.elozoFut = !!f.fut;
      if (lentVolt) setTimeout(() => { const el = this.futNaplo?.nativeElement; if (el) el.scrollTop = el.scrollHeight; });
    } catch { /* a figyelő épp nem érhető el – a következő körben újra */ }
    this.futasIdozito = setTimeout(() => this.futasFrissit(), this.futas?.fut ? 2000 : 15000);
  }

  async futtat() {
    this.inditas = true; this.futHiba = '';
    try {
      await firstValueFrom(this.http.post('/api/jogszabalyfigyelo/futtat', { mod: this.futMod }));
      this.futasNyitva = true;
      setTimeout(() => this.futasFrissit(), 1500);
    } catch (e: any) { this.futHiba = e?.error?.error || 'A kör nem indult el.'; }
    this.inditas = false;
  }

  /** A naplósor kinézete: fázis, lépés, gondolat, hiba. */
  sorOsztaly(s: string) {
    if (s.startsWith('▶')) return 'fazis';
    if (s.startsWith('────')) return 'fej';
    if (/HIBA|hiba|megszakadt|visszaállítva/.test(s)) return 'hibas';
    if (s.includes(' › ')) return 'lepes';
    if (s.includes(' 💬 ')) return 'gondolat';
    return '';
  }

  async betolt() {
    this.tolt = true; this.hiba = '';
    try { this.adat = await firstValueFrom(this.http.get('/api/jogszabalyfigyelo/adat')); }
    catch (e: any) { this.hiba = e?.error?.error || 'A jogszabályfigyelő adatai nem érhetők el.'; }
    this.tolt = false;
  }

  lapValt(l: Lap) { this.lap = l; try { localStorage.setItem('jf-lap', l); } catch { /* nincs tár */ } }

  // ── adat ────────────────────────────────────────────────────────────────
  get tetelek(): any[] { return this.adat?.tetelek?.tetelek || []; }
  get kulcsszamok(): any[] { return this.adat?.kulcsszamok?.sorok || []; }
  get kovetelmenyek(): any[] { return this.adat?.kovetelmenyek?.kovetelmenyek || []; }
  get kovKategoriak(): string[] { return [...new Set(this.kovetelmenyek.map((k) => k.kategoria))]; }
  get kovSzurt(): any[] {
    const q = this.osz.q.trim().toLowerCase();
    return this.kovetelmenyek.filter((k) => (!this.osz.kat || k.kategoria === this.osz.kat)
      && (!this.osz.mf || k.megfeleles === this.osz.mf) && (!q || JSON.stringify(k).toLowerCase().includes(q)));
  }
  kovKategoriaban(c: string): any[] { return this.kovSzurt.filter((k) => k.kategoria === c); }
  get kovSzurtKategoriak(): string[] { return [...new Set(this.kovSzurt.map((k) => k.kategoria))]; }
  kovDb(m: string) { return this.kovetelmenyek.filter((k) => k.megfeleles === m).length; }
  mfOsztaly(m: string) { return MEGFELELES[m]?.[0] || ''; }
  mfNev(m: string) { return MEGFELELES[m]?.[1] || m; }
  kireNev(k: string) { return KIRE[k] || k; }
  /** A SULINFORM-ot érintő nyitott változások, fontosság, majd hatálybalépés szerint. */
  get oktatasiTetelek(): any[] {
    return this.tetelek.filter((t) => (t.modulok || []).includes('SULINFORM') && this.nyitott(t))
      .sort((a, b) => (FONT_SORREND[a.fontossag] ?? 3) - (FONT_SORREND[b.fontossag] ?? 3)
        || String(a.hatalyba || '9999').localeCompare(String(b.hatalyba || '9999')));
  }
  get jelzesek(): any[] { return this.adat?.jelzesek?.jelzesek || []; }
  get jogszabalyok(): any[] {
    return Object.values(this.adat?.jogszabalyok || {})
      .sort((a: any, b: any) => (b.jovobeni?.length || 0) - (a.jovobeni?.length || 0) || String(a.cim).localeCompare(String(b.cim)));
  }
  get forrasok(): any[] { return Object.entries(this.adat?.forrasok || {}).map(([k, v]: any) => ({ id: k, ...v })); }
  get futasok(): any[] { return [...(this.adat?.futasok || [])].reverse(); }
  get modulok(): [string, any][] { return Object.entries(this.adat?.modulok || {}); }
  get jel(): Record<string, any> { return this.adat?.jelolesek || {}; }

  modulNev(m: string) { return this.adat?.modulok?.[m]?.nev || m; }
  allapotNev(a: string) { return ALL_NEV[a] || a; }

  frissult(t: any) { const s = this.jel[t.id]; return !!(s?.mikor && t.utolso_valtozas && t.utolso_valtozas > s.mikor); }
  nyitott(t: any) { return !['kesz', 'nem_erint'].includes(this.jel[t.id]?.allapot) && t.allapot !== 'elvetve'; }
  olvasatlan(t: any) { return !this.jel[t.id]?.allapot || this.frissult(t); }

  get olvasatlanDb() { return this.tetelek.filter((t) => this.olvasatlan(t)).length; }
  get hibasForras() { return this.forrasok.filter((f) => f.hiba).length; }
  get mutatok() {
    const ny = this.tetelek.filter((t) => this.nyitott(t));
    return [
      { n: this.olvasatlanDb, sz: 'olvasatlan / frissült tétel' },
      { n: ny.filter((t) => t.fontossag === 'magas').length, sz: 'nyitott, magas fontosságú' },
      { n: ny.filter((t) => t.hatalyba && t.hatalyba >= this.ma && this.napig(t.hatalyba) <= 90).length, sz: 'hatályba lép 90 napon belül' },
      { n: ny.filter((t) => ['pletyka', 'bejelentve', 'tervezet'].includes(t.allapot)).length, sz: 'bejelentve, még nincs elfogadott szöveg' },
      { n: this.hibasForras, sz: 'hibás forrás', piros: this.hibasForras > 0 },
    ];
  }
  get kozelgo(): any[] {
    return this.tetelek.filter((t) => this.nyitott(t) && t.hatalyba && t.hatalyba >= this.ma)
      .sort((a, b) => a.hatalyba.localeCompare(b.hatalyba));
  }
  get evek(): string[] {
    return [...new Set(this.kulcsszamok.flatMap((s) => Object.keys(s.ertekek || {})))].sort();
  }

  napig(d: string) { return Math.round((new Date(d).getTime() - new Date(this.ma).getTime()) / 864e5); }
  hatalyCimke(t: any) {
    if (!t.hatalyba) return 'hatály: nem ismert';
    const n = this.napig(t.hatalyba);
    return (t.hatalyba_biztos === false ? '≈ ' : '') + (n < 0 ? `hatályos ${t.hatalyba}-tól` : `${t.hatalyba} (${n} nap)`);
  }
  hatalyOsztaly(t: any) {
    if (!t.hatalyba) return '';
    const n = this.napig(t.hatalyba);
    return n < 0 ? '' : n <= 30 ? 'piros' : n <= 90 ? 'sarga' : 'kek';
  }
  lepcso(a: string) {
    const i = ALLAPOTOK.findIndex((x) => x[0] === a);
    return ALLAPOTOK.map(([k, n], j) => ({ n, o: j < i ? 'kesz' : j === i ? 'itt' : '' }));
  }
  ertekOsztaly(a: string) {
    return ['hatalyos', 'kihirdetve'].includes(a) ? 'zold' : ['elfogadva', 'benyujtva'].includes(a) ? 'kek' : 'sarga';
  }
  // Csak számszerű értékeket vetünk össze („nincs tárolva” stb. nem eltérés) – mint a figyelő oldalán.
  private elsoSzam(x: any) {
    const m = String(x ?? '').replace(/(\d)\s+(?=\d{3}\b)/g, '$1').match(/^\s*(\d+(?:[.,]\d+)?)/);
    return m ? Number(m[1].replace(',', '.')) : null;
  }
  elter(s: any, ev: string) {
    if (ev !== this.ma.slice(0, 4)) return false;
    if (s.elter === true || s.elter === false) return s.elter;
    const a = this.elsoSzam(s.program_erteke), b = this.elsoSzam(s.ertekek?.[ev]?.ertek);
    return a != null && b != null && a !== b;
  }
  datumIdo(s: string) { return String(s || '').slice(0, 16).replace('T', ' '); }

  // ── változások lista ──────────────────────────────────────────────────────
  get lista(): any[] {
    const q = this.sz.q.trim().toLowerCase();
    const l = this.tetelek.filter((t) =>
      (!this.sz.modul || (t.modulok || []).includes(this.sz.modul))
      && (!this.sz.allapot || t.allapot === this.sz.allapot)
      && (!this.sz.font || t.fontossag === this.sz.font)
      && (this.sz.sajat === '' ? true : this.sz.sajat === 'nyitott' ? this.nyitott(t)
        : this.sz.sajat === 'olvasatlan' ? this.olvasatlan(t) : this.jel[t.id]?.allapot === this.sz.sajat)
      && (!q || JSON.stringify(t).toLowerCase().includes(q)));
    const hat = (a: any, b: any) => String(a.hatalyba || '9999').localeCompare(String(b.hatalyba || '9999'));
    return l.sort(this.sz.rend === 'friss' ? (a, b) => String(b.utolso_valtozas || '').localeCompare(String(a.utolso_valtozas || ''))
      : this.sz.rend === 'hataly' ? hat
      : (a, b) => (FONT_SORREND[a.fontossag] ?? 3) - (FONT_SORREND[b.fontossag] ?? 3) || hat(a, b));
  }
  ugras(id: string) {
    this.sz = { ...this.sz, q: '', sajat: '', modul: '', allapot: '', font: '' };
    this.lapValt('valtozasok');
    setTimeout(() => document.getElementById('jf-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  /** Szűk markdown → HTML: listák, **félkövér**, `kód`, linkek; minden más escape-elve. */
  md(s: string): string {
    const e = (x: string) => x.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    let ki = ''; let lista = false;
    for (const r of e(String(s || '')).split('\n')) {
      const l = r.match(/^\s*[-*]\s+(.*)/);
      if (l && !lista) { ki += '<ul>'; lista = true; }
      if (!l && lista) { ki += '</ul>'; lista = false; }
      const sz = (l ? l[1] : r).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/(https?:\/\/[^\s<)]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      ki += l ? `<li>${sz}</li>` : (sz.trim() ? `<div>${sz}</div>` : '');
    }
    return ki + (lista ? '</ul>' : '');
  }

  async jelol(t: any, allapot?: string, megjegyzes?: string) {
    const elozo = this.jel[t.id] || {};
    const uj = { allapot: allapot ?? elozo.allapot ?? '', megjegyzes: megjegyzes ?? elozo.megjegyzes ?? '' };
    try {
      await firstValueFrom(this.http.put('/api/jogszabalyfigyelo/allapot', { id: t.id, ...uj }));
      this.adat.jelolesek = { ...this.jel, [t.id]: { ...uj, mikor: new Date().toISOString() } };
    } catch (e: any) { this.hiba = e?.error?.error || 'A jelölés mentése nem sikerült.'; }
  }

  async masol(t: any) {
    const szoveg = `## ${t.cim}\n\n`
      + `- Állapot: ${this.allapotNev(t.allapot)}${t.hatalyba ? `, hatályba: ${t.hatalyba}${t.hatalyba_biztos === false ? ' (várható)' : ''}` : ''}\n`
      + `- Modulok: ${(t.modulok || []).join(', ')}\n`
      + (t.jogszabalyok?.length ? `- Jogszabály: ${t.jogszabalyok.join('; ')}\n` : '')
      + `\n${t.osszefoglalo || ''}\n\n`
      + (t.parameterek?.length ? `### Paraméterek\n${t.parameterek.map((p: any) => `- ${p.nev}: ${p.regi ?? '–'} → ${p.uj ?? '?'}${p.hol ? ` (${p.hol})` : ''}`).join('\n')}\n\n` : '')
      + (t.fejlesztesi_feladat ? `### Teendő\n${t.fejlesztesi_feladat}\n\n` : '')
      + (t.forrasok?.length ? `### Források\n${t.forrasok.map((f: any) => `- ${f.cim || ''} ${f.url}`).join('\n')}\n` : '');
    try { await navigator.clipboard.writeText(szoveg); this.masolva = t.id; setTimeout(() => (this.masolva = ''), 1600); } catch { /* nincs vágólap */ }
  }
}
